import { faArrowLeft, faArrowRight } from "@fortawesome/pro-regular-svg-icons";

import { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import { SelfAssessmentStepOption } from "@/types/self-assessment";

import FormContainer from "@/pages/Public/components/FormContainer";
import { Icon } from "@/components";

import SelfAssessmentStepIndicator from "./SelfAssessmentStepIndicator";

import SelfAssessmentOption, { SelectableOption } from "./SelfAssessmentOption";
import { AllQuestionsFromToDosAndSubtasks } from "./SelfAssessmentFlow";

interface SelfAssessmentStepsProps {
	stepInfo?: AllQuestionsFromToDosAndSubtasks;
	activeStep: number;
	totalStepLength: number;
	currentAnswer?: SelfAssessmentStepOption | null;
	handleNext: (currentAnswer: SelfAssessmentStepOption) => void;
	handleBack: (currentAnswer: SelfAssessmentStepOption | null) => void;
	changeStepWhenSkipHandler?: (currentAnswer: SelfAssessmentStepOption) => void;
	isLoading: boolean;
	subTasksOfCurrentStepLength: number;
	activeSubStep: number;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	allSubTasksOfCurrentStep: any;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	formError?: any;
	answers?: SelfAssessmentStepOption[];
	setAnswers: React.Dispatch<React.SetStateAction<SelfAssessmentStepOption[]>>;
}

export default function SelfAssessmentSteps({
	activeStep,
	handleBack,
	handleNext,
	isLoading,
	totalStepLength,
	subTasksOfCurrentStepLength,
	allSubTasksOfCurrentStep,
	stepInfo,
	changeStepWhenSkipHandler,
	currentAnswer,
	activeSubStep,
	formError,
	answers,
	setAnswers,
}: SelfAssessmentStepsProps) {
	const { t } = useTranslation();
	const ts = (key: string, options = {}) =>
		t(`questionnaire.self-assessment.progress.${key}`, options);
	const [answer, setAnswer] = useState<SelfAssessmentStepOption | null>(currentAnswer ?? null);
	const [selectedOption, setSelectedOption] = useState<SelectableOption | null>(null);
	const [measure, setMeasure] = useState("");
	const [measureDescription, setMeasureDescription] = useState("");
	const [requirement, setRequirement] = useState("");

	useEffect(() => {
		setRequirement("");
		if (!stepInfo?.isSubtask) {
			setMeasure(stepInfo?.category ?? stepInfo?.title ?? "");
			setMeasureDescription(stepInfo?.description ?? "");
			setRequirement(stepInfo?.requirement ?? "");
		}

		setRequirement(stepInfo?.isSubtask ? "" : stepInfo?.requirement ?? "");
	}, [stepInfo]);

	// Reset selectedOption when step changes
	useEffect(() => {
		setSelectedOption(null);
		setAnswer(null);
	}, [stepInfo?.todoId, stepInfo?.subtaskId]);

	return (
		<div className="flex flex-col h-fit mt-24 mb-24 spacy-y-4 max-w-[100%] gap-1">
			<SelfAssessmentStepIndicator currentStepIndex={activeStep - 1} totalSteps={totalStepLength} />
			<FormContainer
				changeActionsDirection
				actionContainerClassName="flex gap-2 !space-y-0 max-w"
				additionalInformation={
					!stepInfo?.isSubtask ? stepInfo?.recommendation : stepInfo?.questionDescription
				}
				descriptionClass="text-primary-gray-lighter text-[14px] leading-[20px] px-2 py-1 rounded-lg bg-[#F7F8FB]"
				disableSecondaryButton={activeStep === 1 && !stepInfo?.isSubtask}
				formContainerClassName="h-fit w-[800px]"
				formError={formError}
				formHeader={
					<>
						<p
							className={`${
								stepInfo?.isSubtask ? "" : "mb-4"
							} text-[14px] font-medium text-primary-gray-lighter`}
						>
							{ts("step", {
								currentStep: activeStep,
								totalLength: totalStepLength,
							})}
						</p>
						{activeSubStep > 0 && subTasksOfCurrentStepLength && (
							<p className="text-[12px] font-medium text-primary-gray-lighter mb-4">
								{ts("subStep", {
									currentStep: activeSubStep,
									totalLength: subTasksOfCurrentStepLength,
								})}
							</p>
						)}
					</>
				}
				handleSubmit={() => {
					if (answer) {
						// Check if skip was selected
						if (selectedOption === "skip") {
							// Handle skip logic - same as before but triggered on "Weiter" click
							const skipAnswer = {
								...answer,
								answer: "no" as const,
								isSkipped: true,
							};

							if (subTasksOfCurrentStepLength > 0) {
								const subAnswers = allSubTasksOfCurrentStep.map(
									(sub: SelfAssessmentStepOption) => ({
										questionId: sub?.todoId ?? 0,
										isSubtask: sub?.isSubtask ?? false,
										subtaskId: sub?.subtaskId ?? "",
										todoId: sub?.todoId ?? 0,
										answer: "no" as const,
										industryIds: sub?.industryIds ?? [],
										companySizeId: sub?.companySizeId ?? null,
										companyRevenueId: sub?.companyRevenueId ?? null,
										isSkipped: sub?.isSkipped ?? false,
									})
								);

								const updatedAnswers = [...(answers ?? [])];

								// Add or update the main task answer
								const mainAnswerIndex = updatedAnswers.findIndex(
									(a) =>
										a.questionId === skipAnswer.questionId &&
										a.isSubtask === skipAnswer.isSubtask &&
										a.subtaskId === skipAnswer.subtaskId
								);

								if (mainAnswerIndex !== -1) {
									updatedAnswers[mainAnswerIndex] = skipAnswer;
								} else {
									updatedAnswers.push(skipAnswer);
								}

								// Add or update each subtask answer
								subAnswers.forEach((newSubAnswer: SelfAssessmentStepOption) => {
									const existingIndex = updatedAnswers.findIndex(
										(a) =>
											a.questionId === newSubAnswer.questionId &&
											a.isSubtask === newSubAnswer.isSubtask &&
											a.subtaskId === newSubAnswer.subtaskId
									);

									if (existingIndex !== -1) {
										updatedAnswers[existingIndex] = newSubAnswer;
									} else {
										updatedAnswers.push(newSubAnswer);
									}
								});

								setAnswers(updatedAnswers);
								handleNext(skipAnswer);
								setAnswer(null);
								setSelectedOption(null);

								if (activeStep === totalStepLength) {
									localStorage.setItem("selfAssessmentAnswers", JSON.stringify(updatedAnswers));
								}
							} else {
								const updatedAnswers = [...(answers ?? [])];
								const existingIndex = updatedAnswers.findIndex(
									(a) =>
										a.questionId === skipAnswer.questionId &&
										a.isSubtask === skipAnswer.isSubtask &&
										a.subtaskId === skipAnswer.subtaskId
								);

								if (existingIndex !== -1) {
									updatedAnswers[existingIndex] = skipAnswer;
								} else {
									updatedAnswers.push(skipAnswer);
								}

								setAnswers(updatedAnswers);
								handleNext(skipAnswer);
								setAnswer(null);
								setSelectedOption(null);

								if (activeStep === totalStepLength) {
									localStorage.setItem("selfAssessmentAnswers", JSON.stringify(updatedAnswers));
								}
							}
						} else {
							// Normal yes/no answer handling
							const updatedAnswers = [...(answers ?? [])];
							const existingIndex = updatedAnswers.findIndex(
								(a) =>
									a.questionId === answer.questionId &&
									a.isSubtask === answer.isSubtask &&
									a.subtaskId === answer.subtaskId
							);

							if (existingIndex !== -1) {
								updatedAnswers[existingIndex] = answer;
							} else {
								updatedAnswers.push(answer);
							}

							setAnswers(updatedAnswers);
							handleNext(answer);
							setAnswer(null);
							setSelectedOption(null);

							if (activeStep === totalStepLength) {
								localStorage.setItem("selfAssessmentAnswers", JSON.stringify(updatedAnswers));
							}
						}
					}
				}}
				isLoading={isLoading}
				measure={measure}
				recommendation={stepInfo?.isSubtask ? "" : stepInfo?.recommendation}
				requirement={requirement}
				secondaryButtonAction={() => {
					handleBack(answer);
					setAnswer(null);
					setSelectedOption(null);
				}}
				secondaryButtonText={
					<p className="text-[14px] text-primary-blue-dark font-medium flex items-center justify-center gap-2">
						<Icon className="w-4 h-3" icon={faArrowLeft} />
						{ts("back")}
					</p>
				}
				secondaryButtonType="button"
				showLogo={false}
				subTaskName={stepInfo?.isSubtask ? stepInfo?.title : ""}
				submitButtonText={
					<p className="text-[14px] text-white font-medium flex items-center justify-center gap-2">
						{ts("next")}
						<Icon className="w-4 h-3 text-white" icon={faArrowRight} />
					</p>
				}
				submitButtonType="button"
				title={stepInfo?.question ? `${t("basics.question")}: ${stepInfo?.question}` : ""}
				titleClass="text-ssm leading-[20px] md:!text-[16px] md:!leading-[20px] !mb-4 mt-6 font-bold"
				toDoDescription={measureDescription}
				toDoName={stepInfo?.isSubtask ? "" : stepInfo?.title}
			>
				<div className="space-y-2 mt-6">
					<SelfAssessmentOption
						isSubtask={stepInfo?.isSubtask ?? false}
						questionId={stepInfo?.todoId ?? 0}
						selected={selectedOption}
						subtaskId={stepInfo?.subtaskId ?? ""}
						todoId={stepInfo?.todoId ?? 0}
						onSelect={(option, isSkipSelection) => {
							if (isSkipSelection) {
								setSelectedOption("skip");
							} else {
								setSelectedOption(option.answer);
							}

							setAnswer({
								questionId: stepInfo?.todoId ?? 0,
								isSubtask: stepInfo?.isSubtask ?? false,
								subtaskId: stepInfo?.subtaskId ?? "",
								todoId: stepInfo?.todoId ?? 0,
								answer: option.answer,
								industryIds: stepInfo?.industryIds ?? [],
								companySizeId: stepInfo?.companySizeIds?.[0] ?? null,
								companyRevenueId: stepInfo?.companyRevenueIds?.[0] ?? null,
								isSkipped: isSkipSelection ?? false,
							});
						}}
					/>
				</div>
			</FormContainer>
		</div>
	);
}
